import { strip as presets } from "@trackmark/tokens";
import styles from "./RatingStrip.module.css";

export type StripSize = keyof typeof presets;
/** Where the strip sits, which decides how the fill inverts. */
export type StripGround = "newsprint" | "well" | "ink";

export interface RatingStripProps {
  /** 1–10, or null when unrated. NEVER 0 — unrated is the absence of a value, not a zero. */
  value: number | null;
  /** Transient hover/live value; overrides `value` visually while present. */
  live?: number | null;
  /** 1–10, the keyboard cursor cell. */
  cursor?: number | null;
  size?: StripSize;
  ground?: StripGround;
  /** Show the 1…10 numerals beneath. Only meaningful at input sizes. */
  numerals?: boolean;
  /** Bonus tracks render faded — they do not count toward the album score. */
  bonus?: boolean;
  onRate?: (value: number) => void;
  onHover?: (value: number | null) => void;
  label?: string;
}

const CELLS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * One component displays every score, everywhere. There is no second visual language for scores
 * in this app — no stars, no donuts, no rings, no gradient bars.
 *
 * Three states per cell: empty (a printed block on a baseline rule), committed (solid ink), and
 * live (solid vermilion, while hovering or during the 250ms hold after a keypress).
 */
export function RatingStrip({
  value,
  live = null,
  cursor = null,
  size = "display",
  ground = "newsprint",
  numerals = false,
  bonus = false,
  onRate,
  onHover,
  label,
}: RatingStripProps) {
  const preset = presets[size];
  const interactive = Boolean(onRate);
  const shown = live ?? value;

  const style = {
    "--cell": `${preset.cell}px`,
    "--gap": `${preset.gap}px`,
    "--height": `${preset.height}px`,
  } as React.CSSProperties;

  /*
   * Mobile is two rows of five 62x56 cells with the numeral INSIDE each one, bottom-aligned
   * (design/README.md §13). A different arrangement rather than a different size: the numerals
   * cannot sit under the strip when the strip is two rows deep, and a 62px cell is a target
   * rather than a bar.
   */
  const twoRows = size === "inputMobile";

  const classes = [
    styles.strip,
    size.startsWith("input") || size === "specimen" ? styles.input : "",
    twoRows ? styles.twoRows : "",
    ground === "well" ? styles.onWell : "",
    ground === "ink" ? styles.onInk : "",
    interactive ? styles.interactive : "",
    bonus ? styles.bonus : "",
  ]
    .filter(Boolean)
    .join(" ");

  const fill = (n: number): "empty" | "set" | "live" => {
    if (live !== null && n <= live) return "live";
    if (live === null && value !== null && n <= value) return "set";
    return "empty";
  };

  const cells = (
    <div
      className={classes}
      style={style}
      role={interactive ? "group" : "img"}
      aria-label={label ?? (value === null ? "Unrated" : `Rated ${value} out of 10`)}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
    >
      {CELLS.map((n) =>
        interactive ? (
          <button
            key={n}
            type="button"
            className={styles.cell}
            data-fill={fill(n)}
            data-cursor={cursor === n ? "true" : undefined}
            onMouseEnter={onHover ? () => onHover(n) : undefined}
            onClick={() => onRate?.(n)}
            aria-label={`Rate ${n}`}
          >
            {/* The numeral is aria-hidden: the button already says "Rate 7", and a screen reader
                reading "Rate 7, 7" is the label twice. */}
            {twoRows && (
              <span className={styles.cellNumeral} data-fill={fill(n)} aria-hidden="true">
                {n}
              </span>
            )}
          </button>
        ) : (
          <div key={n} className={styles.cell} data-fill={fill(n)} data-cursor={cursor === n ? "true" : undefined} />
        ),
      )}
    </div>
  );

  if (!numerals) return cells;

  return (
    <div>
      {cells}
      <div className={styles.numerals} style={{ ...style, fontSize: `${preset.numeral}px` }}>
        {CELLS.map((n) => (
          <div
            key={n}
            className={styles.numeral}
            data-state={n === shown ? (live !== null ? "live" : "set") : undefined}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}
